import React from "react";
import PropTypes from "prop-types";
import useDocumentFirestore from "@web/hooks/useDocumentFirestore";
import DraftEditor from "./DraftEditor";
import {deletePath, setPath, updatePath} from "@web/lib/firestore.db";
import now from "lodash/now";
import {api} from "@web/lib/api";
import {notification} from "antd";
import useChatStreamListener from "@web/hooks/useChatStreamListener";
import {chatAppAgents} from "@oven/app-thescribe/src/dashboard.config";
import {UserContext} from '@web/hooks/context';

DraftEditorManager.propTypes = {
  draftPath: PropTypes.string,
  collapsed: PropTypes.bool,
  onAddContextPath: PropTypes.func,
  onRemoveContextPath: PropTypes.func,
  onClearEmailDraft: PropTypes.func,
};

function DraftEditorManager(props) {
  const [userId] = React.useContext(UserContext);
  const {draftPath} = props;
  const [chat, chatPath] = useChatStreamListener();
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);
  const [finished, setFinished] = React.useState(false);
  const [draft, loadingDraft, error, snap] = useDocumentFirestore(draftPath);
  const [parentEmailMessage] = useDocumentFirestore(draft?.parentEmailPath);
  const [text, setText] = React.useState(draft?.message || "");
  const [collapsed, setCollapsed] = React.useState(props.collapsed);
  const addEmail = React.useCallback((email) => {
    console.log("addEmail:", email);
    const alreadyInDistro = draft
    .toEmails
    .some((member) =>
      member.email === email);

    if (alreadyInDistro) {
      return;
    }

    return updatePath(draftPath,{
      ...draft,
      toEmails: [
        ...draft.toEmails,
        {email, name: email},
      ],
      updatedTs: now(),
      date: now(),
    });
  }, [draft]);
  const updateSendAction = React.useCallback((newAction) => {
    console.log("updateSendAction:", newAction);
    return updatePath(draftPath, {
      ...draft,
      action: newAction,
      updatedTs: now(),
      date: now(),
    });
  }, [draft]);
  const save = React.useCallback(() => {
    console.log("Saving draft", draftPath);
    return setPath(
      props.draftPath, {
        ...draft,
        message: text,
        updatedTs: now(),
        date: now(),
      });
  }, [draft]);
  const send = React.useCallback(() => {
    console.log("Sending email", draftPath);
      setSending(true);
      return setPath(
        props.draftPath, {
          ...draft,
          message: text,
          updatedTs: now(),
          submitTs: now(),
        },
      )
      .then(() =>
        api.POST(
          "http://127.0.0.1:5001/admin-85663/us-west1/user-sendEmail",
          {path: draftPath}),
      )
      .then((res) => {
        console.log("Response", res);
        if (res.error) {
          setSending(false);
          setSendError(res);
          return;
        }
        notification
        .success({
          message: "Email sent",
          description: "Your email has been sent successfully",
        });
        setFinished(true);

        props.onClearActiveEmailDraft?.();
      })
      .catch((err) => {
        setSending(false);
        notification
        .error({
          message: "An error occurred",
          description: "We are looking into it",
        });
      });
  },[draft, draftPath]);
  const collapse = React.useCallback(() => {
    console.log("Collapsed:", collapsed);
      setCollapsed(!collapsed);
    }, [collapsed]);
  const deleteDraft = React.useCallback(() => {
    deletePath(draftPath)
    .then(() => props.onClearEmailDraft?.());
  }, [draftPath]);

  const onTextChange = React.useCallback((message) => {
    console.log("onTextChange", message);
    setText(message);
  }, []);
  const getDistroLists = React.useCallback(() => {
    if (!draft) {
      return [[], []];
    }
    return [
      draft.toEmails || [],
      draft.ccEmails || [],
    ];
    // switch (draft.action) {
    //   case "reply": {
    //     const to =
    //       parentEmailMessage?.replyTo ||
    //       parentEmailMessage?.from;
    //
    //     return [
    //       [to]
    //       .filter(Boolean),
    //       [],
    //     ];
    //   }
    //   case "reply-all": {
    //     const to =
    //       parentEmailMessage?.replyTo ||
    //       parentEmailMessage?.from;
    //
    //     return [
    //       [to].filter(Boolean),
    //       ...(parentEmailMessage?.to || []).filter(({email}) => email !== to.email),
    //     ];
    //   }
    //   default: {
    //     return [[], []];
    //   }
    // }
  }, [draft, parentEmailMessage]);

  React.useEffect(() => {
    // console.log("DraftEditorManager:useEffect:draft:", draft?.message);
    !!draft &&
    setText(draft?.message || "");
  }, [draft?.message]);

  React.useEffect(() => {
    finished ?
      props.onRemoveContextPath?.(draftPath) :
      props.onAddContextPath?.(draftPath);
  }, [finished]);

  React.useEffect(() => {
    if (!chat) {
      return;
    }
    const draftAnswer =
      chat.answers.find((answer) =>
      chatAppAgents.emailWriter.agentIds.includes(answer.agentId));

    draftAnswer && // todo: Use startTransition() for performance if needed
    setText(draftAnswer.text);
  }, [chat, chatPath]);

  React.useEffect(() => {
    !loadingDraft &&
    !draft &&
    setFinished(true);
  }, [loadingDraft, draft]);

  React.useEffect(() => {
    finished &&
    props.onClearEmailDraft?.();
  }, [finished]);

  if (loadingDraft) {
    return null;
  }

  return <DraftEditor
    collapsed={!!props.collapsed}
    draft={draft}
    parentEmailMessage={parentEmailMessage}
    sending={sending}
    finished={finished}
    onAddEmail={addEmail}
    onClickSave={save}
    onClickSend={send}
    onClickCollapse={collapse}
    onClickDelete={deleteDraft}
    onTextChange={onTextChange}
    onChangeSendAction={updateSendAction}
    getDistroLists={getDistroLists}
    text={text}
  />;
}

export default DraftEditorManager;
